import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, CheckCircle2, ExternalLink } from 'lucide-react';
import Button from './Button';
import GoogleRecaptcha from './GoogleRecaptcha';
import { bookingDetails, company, links } from '../data/siteData';

const CALENDLY_SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_CSS_HREF = 'https://assets.calendly.com/assets/external/widget.css';

// Subresource Integrity hashes for the Calendly widget assets.
//
// IMPORTANT: Calendly does NOT version-pin their CDN URLs — the same URL serves
// updated content over time. When Calendly updates widget.js or widget.css, the
// browser will refuse to execute the resource until these hashes are updated.
//
// Set these via env vars in your hosting platform (Netlify):
//   REACT_APP_CALENDLY_SCRIPT_SRI
//   REACT_APP_CALENDLY_CSS_SRI
//
// To compute the current hashes locally, run:
//   npm run sri:calendly
//
// If the hash is missing, the script loads WITHOUT SRI verification (current
// behavior). If the hash is present but mismatches, the browser blocks the
// script and the component falls back to "link-only" mode with a button that
// opens Calendly in a new tab.
const CALENDLY_SCRIPT_SRI = process.env.REACT_APP_CALENDLY_SCRIPT_SRI || '';
const CALENDLY_CSS_SRI = process.env.REACT_APP_CALENDLY_CSS_SRI || '';

let calendlyScriptPromise;

function ensureCalendlyAssets() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Calendly can only load in the browser.'));
  }

  if (!document.querySelector(`link[href="${CALENDLY_CSS_HREF}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CALENDLY_CSS_HREF;
    if (CALENDLY_CSS_SRI) {
      link.integrity = CALENDLY_CSS_SRI;
      link.crossOrigin = 'anonymous';
    }
    document.head.appendChild(link);
  }

  if (window.Calendly) {
    return Promise.resolve(window.Calendly);
  }

  if (!calendlyScriptPromise) {
    calendlyScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);

      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.Calendly), { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = CALENDLY_SCRIPT_SRC;
      script.async = true;
      if (CALENDLY_SCRIPT_SRI) {
        script.integrity = CALENDLY_SCRIPT_SRI;
        script.crossOrigin = 'anonymous';
      }
      script.onload = () => resolve(window.Calendly);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  return calendlyScriptPromise;
}

function buildCalendlyUrl(baseUrl) {
  if (!baseUrl) {
    return '';
  }

  try {
    const url = new URL(baseUrl);
    url.searchParams.set('hide_gdpr_banner', '1');

    if (!url.searchParams.has('primary_color')) {
      url.searchParams.set('primary_color', '087f98');
    }

    if (!url.searchParams.has('text_color')) {
      url.searchParams.set('text_color', '061844');
    }

    if (!url.searchParams.has('background_color')) {
      url.searchParams.set('background_color', 'ffffff');
    }

    return url.toString();
  } catch (error) {
    return baseUrl;
  }
}

export default function CalendlyBooking({ calendlyUrl = links.calendly }) {
  const containerRef = useRef(null);
  const embedUrl = useMemo(() => buildCalendlyUrl(calendlyUrl), [calendlyUrl]);
  const [status, setStatus] = useState(embedUrl ? 'captcha-required' : 'missing');
  const [useMobileIframe, setUseMobileIframe] = useState(false);
  const [bookingCaptchaVerified, setBookingCaptchaVerified] = useState(false);

  const handleBookingRecaptchaVerify = useCallback(() => {
    setBookingCaptchaVerified(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mobileQuery = window.matchMedia('(max-width: 640px)');
    const syncMobileMode = () => setUseMobileIframe(mobileQuery.matches);

    syncMobileMode();

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', syncMobileMode);
      return () => mobileQuery.removeEventListener('change', syncMobileMode);
    }

    mobileQuery.addListener(syncMobileMode);
    return () => mobileQuery.removeListener(syncMobileMode);
  }, []);

  useEffect(() => {
    if (!embedUrl) {
      setStatus('missing');
      return undefined;
    }

    if (!bookingCaptchaVerified) {
      setStatus('captcha-required');
      return undefined;
    }

    if (useMobileIframe) {
      setStatus('ready');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    ensureCalendlyAssets()
      .then(() => {
        if (cancelled || !containerRef.current) {
          return;
        }

        containerRef.current.innerHTML = '';

        if (window.Calendly?.initInlineWidget) {
          window.Calendly.initInlineWidget({
            url: embedUrl,
            parentElement: containerRef.current,
          });
          setStatus('ready');
          return;
        }

        setStatus('link-only');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('link-only');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bookingCaptchaVerified, embedUrl, useMobileIframe]);

  return (
    <section className="section shell calendly-section">
      <div id="booking" className="calendly-card">
        <div className="calendly-card__content">
          <p className="eyebrow">Book a Consultation</p>
          <h2>{bookingDetails.title}</h2>
          <p>{bookingDetails.description}</p>

          <div className="calendly-benefits">
            {bookingDetails.bullets.map((item) => (
              <span key={item}>
                <CheckCircle2 size={17} /> {item}
              </span>
            ))}
          </div>

          {/*<div className="button-row top-gap-md">*/}
          {/*  {embedUrl ? (*/}
          {/*    <Button href={embedUrl} variant="secondary" target="_blank" rel="noreferrer">*/}
          {/*      Open Calendly <ExternalLink size={16} />*/}
          {/*    </Button>*/}
          {/*  ) : (*/}
          {/*    <Button href={`mailto:${company.email}`} variant="secondary">*/}
          {/*      Email BrainTorch™ Bio*/}
          {/*    </Button>*/}
          {/*  )}*/}
          {/*</div>*/}
        </div>

        <div className="calendly-card__embed" aria-live="polite">
          {embedUrl ? (
            <>
              {bookingCaptchaVerified ? (
                useMobileIframe ? (
                  <div className="calendly-inline-frame calendly-inline-frame--direct">
                    <iframe
                      title={`${company.name} Calendly booking calendar`}
                      src={embedUrl}
                      loading="lazy"
                      scrolling="yes"
                    />
                  </div>
                ) : (
                  <div ref={containerRef} className="calendly-inline-frame" />
                )
              ) : (
                <div id="booking-recaptcha-gate" className="calendly-recaptcha-gate">
                  <CalendarDays size={38} />
                  <strong>Complete the security check to load the booking calendar.</strong>
                  <span>This helps protect the consultation calendar from bots and spam.</span>
                  <GoogleRecaptcha
                    id="booking-recaptcha"
                    className="recaptcha-field--centered"
                    label="Booking calendar security check"
                    onVerify={handleBookingRecaptchaVerify}
                  />
                </div>
              )}
              {bookingCaptchaVerified && status === 'loading' ? (
                <div className="calendly-placeholder">
                  <CalendarDays size={34} />
                  <strong>Loading booking calendar...</strong>
                  <span>Please wait while Calendly loads.</span>
                </div>
              ) : null}
              {bookingCaptchaVerified && status === 'link-only' ? (
                <div className="calendly-placeholder">
                  <CalendarDays size={34} />
                  <strong>Calendly could not load inline.</strong>
                  <span>Use the button to open the booking page in a new tab.</span>
                </div>
              ) : null}
            </>
          ) : (
            <div className="calendly-placeholder calendly-placeholder--missing">
              <CalendarDays size={38} />
              <strong>Calendly booking is ready for your live link.</strong>
              <span>
                Add the final Calendly event URL as <code>REACT_APP_CALENDLY_URL</code> or update <code>links.calendly</code> in <code>src/data/siteData.js</code>.
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
